// AI Code-Review insights — is the `ai-code-reviewer` agent trusted and effective?
// Coverage, findings/MR, severity mix, approval rate, time-to-review, and the key
// signal: acceptance rate (resolved ÷ resolvable findings). Data:
// GET /api/pulse/code-review/insights; POST /api/pulse/code-review/scan to rescan.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

const SEV_COLORS = { critical: '#ef4444', medium: '#f59e0b', minor: '#eab308' }

function Kpi({ label, sublabel, value, unit }) {
    return (
        <div
            style={{
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '10px',
                padding: '16px',
                flex: '1 1 150px',
                minWidth: '150px',
            }}
        >
            <div style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '13px' }}>{label}</div>
            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', marginBottom: '10px' }}>{sublabel}</div>
            <div style={{ color: PULSE_COLORS.text, fontSize: '26px', fontWeight: 700 }}>
                {value != null ? value : '—'}
                {value != null && <span style={{ fontSize: '13px', fontWeight: 500 }}>{unit}</span>}
            </div>
        </div>
    )
}

export default function CodeReviewInsights({ refreshSignal }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [scanning, setScanning] = useState(false)
    const [error, setError] = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const r = await fetch('/api/pulse/code-review/insights?weeks=12')
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? j?.message ?? `insights ${r.status}`)
            setData(j)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load, refreshSignal])

    const rescan = useCallback(async () => {
        setScanning(true)
        setError(null)
        try {
            const r = await fetch('/api/pulse/code-review/scan?days=90', { method: 'POST' })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? j?.message ?? `scan ${r.status}`)
            await load()
        } catch (e) {
            setError(e.message)
        } finally {
            setScanning(false)
        }
    }, [load])

    const s = data?.summary
    const sevData = useMemo(
        () =>
            s
                ? [
                      { name: 'Critical', key: 'critical', value: s.severity?.critical || 0 },
                      { name: 'Medium', key: 'medium', value: s.severity?.medium || 0 },
                      { name: 'Minor', key: 'minor', value: s.severity?.minor || 0 },
                  ].filter((x) => x.value > 0)
                : [],
        [s]
    )

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        🤖 AI Code Reviewer — trusted &amp; effective?
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                        `ai-code-reviewer` across 3mp repos, merged MRs (last 12 weeks). Acceptance rate = findings
                        developers actually resolved.
                    </p>
                </div>
                <button
                    onClick={rescan}
                    disabled={scanning}
                    style={{
                        background: PULSE_COLORS.primary,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 14px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: scanning ? 'default' : 'pointer',
                        opacity: scanning ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {scanning ? 'Scanning…' : '↻ Rescan'}
                </button>
            </div>

            {loading && <div style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>Loading…</div>}
            {error && <div style={{ color: PULSE_COLORS.danger, fontSize: '13px', marginBottom: '12px' }}>⚠ {error}</div>}

            {s && (
                <>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                        <Kpi label="Review coverage" sublabel={`${s.reviewedMrs}/${s.totalMrs} merged MRs`} value={s.coveragePct} unit="%" />
                        <Kpi label="Findings / MR" sublabel="on reviewed MRs" value={s.avgFindingsPerReviewedMr} unit="" />
                        <Kpi label="Approved" sublabel="verdict on reviewed MRs" value={s.approvedPct} unit="%" />
                        <Kpi label="Time to review" sublabel="median, MR open → 1st note" value={s.medianTimeToReviewHours} unit="h" />
                        <Kpi
                            label="Acceptance rate"
                            sublabel={`${s.resolvedFindings}/${s.resolvableFindings} findings resolved`}
                            value={s.acceptanceRatePct}
                            unit="%"
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {/* Severity donut */}
                        <div style={{ flex: '1 1 280px', minWidth: '280px' }}>
                            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', marginBottom: '4px' }}>
                                Findings by severity
                            </div>
                            {sevData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={sevData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85}>
                                            {sevData.map((d) => (
                                                <Cell key={d.key} fill={SEV_COLORS[d.key]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                background: PULSE_COLORS.bgInner,
                                                border: `1px solid ${PULSE_COLORS.border}`,
                                                borderRadius: '8px',
                                                color: PULSE_COLORS.text,
                                            }}
                                        />
                                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ color: PULSE_COLORS.textSubtle, fontSize: '13px', padding: '40px 0' }}>
                                    No severity-tagged findings in range.
                                </div>
                            )}
                        </div>

                        {/* Weekly trend */}
                        <div style={{ flex: '1 1 380px', minWidth: '380px' }}>
                            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', marginBottom: '4px' }}>
                                Coverage % &amp; findings/MR by week
                            </div>
                            <ResponsiveContainer width="100%" height={220}>
                                <LineChart data={data.trend} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                                    <XAxis dataKey="week" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 10 }} />
                                    <YAxis yAxisId="left" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} />
                                    <Tooltip
                                        contentStyle={{
                                            background: PULSE_COLORS.bgInner,
                                            border: `1px solid ${PULSE_COLORS.border}`,
                                            borderRadius: '8px',
                                            color: PULSE_COLORS.text,
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                                    <Line yAxisId="left" type="monotone" dataKey="coveragePct" name="Coverage %" stroke="#3b82f6" strokeWidth={2} />
                                    <Line yAxisId="right" type="monotone" dataKey="findingsPerMr" name="Findings/MR" stroke="#a78bfa" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Per-repo table */}
                    {data.byRepo?.length > 0 && (
                        <div style={{ marginTop: '20px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}`, color: PULSE_COLORS.textMuted }}>
                                        <th style={{ textAlign: 'left', padding: '8px 12px 8px 0', fontWeight: 500 }}>Repo</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Merged MRs</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Coverage</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Findings</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 500 }}>Acceptance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.byRepo.map((r) => (
                                        <tr key={r.repo} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                                            <td style={{ padding: '8px 12px 8px 0', color: PULSE_COLORS.text }}>{r.repo}</td>
                                            <td style={{ textAlign: 'right', padding: '8px 12px', color: PULSE_COLORS.text }}>{r.totalMrs}</td>
                                            <td style={{ textAlign: 'right', padding: '8px 12px', color: PULSE_COLORS.textMuted }}>
                                                {r.coveragePct != null ? `${r.coveragePct}%` : '—'}
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '8px 12px', color: PULSE_COLORS.textMuted }}>{r.findingsTotal}</td>
                                            <td style={{ textAlign: 'right', padding: '8px 12px', color: PULSE_COLORS.textMuted }}>
                                                {r.acceptanceRatePct != null ? `${r.acceptanceRatePct}%` : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
